// @ts-strict-ignore
import { ElementRef } from '@angular/core';
import * as Handsontable from 'handsontable';
import { LabelEditorUtils } from './label-editor-utils';
import { LabelEditorComponent } from './label-editor.component';
import { ISpanData } from './span-data';
import { ConstraintCheckUtils } from './constraint-check-validation/constraint-check-utils';
import Quill from 'quill';

export class QuillTableEditorFactory {

  public static create(labelEditorComponent: LabelEditorComponent, labelEditor: ElementRef, editor: Quill): any {
    const quillTableEditor: any = Handsontable.editors.BaseEditor.prototype.extend();

    quillTableEditor.prototype.labelEditorComponent = labelEditorComponent;
    quillTableEditor.prototype.labelEditor = labelEditor;
    quillTableEditor.prototype.editor = editor;

    quillTableEditor.prototype.init = function () {
      this.currentValue = null; // currentValue is used to store current value of table editor, because of reusing quill editor
      this.select = this.labelEditor.nativeElement;
      Handsontable.dom.addClass(this.select, 'htQuillEditor');
      this.select.style.display = 'none';
      this.instance.rootElement.appendChild(this.select);
      this.registerHooks();
    };

    quillTableEditor.prototype.registerHooks = function () {
      this.instance.addHook('afterScrollHorizontally', () => this.refreshDimensions());
      this.instance.addHook('afterScrollVertically', () => this.refreshDimensions());
      this.instance.addHook('afterColumnResize', () => this.refreshDimensions());
      this.instance.addHook('afterRowResize', () => this.refreshDimensions());
    };

    quillTableEditor.prototype.getValue = function () {
      if (this._opened) {
        ConstraintCheckUtils.clearErrorHighlightsInEditor(this.editor, true);
        this.editor.update('api');
        this.currentValue = this.select.querySelector('.ql-editor').innerHTML;
      }
      this.currentValue = LabelEditorUtils.applyCustomFormatSettingsToHTML5Data(
        this.currentValue,
        this.labelEditorComponent.currentlyEditedSpanData.autoFormatByTextCheck && this.labelEditorComponent.currentlyEditedSpanDataCheckResults
          ? this.labelEditorComponent.currentlyEditedSpanDataCheckResults.fontSize
          : NaN,
        this.labelEditorComponent.currentlyEditedSpanData.labelFormat.direction,
        this.labelEditorComponent.currentlyEditedSpanData.labelFormat.verticalAlign,
        this.labelEditorComponent.currentlyEditedSpanData.autoFormatByTextCheck &&
          this.labelEditorComponent.currentlyEditedSpanDataCheckResults &&
          !isNaN(this.labelEditorComponent.currentlyEditedSpanDataCheckResults.textCompression)
          ? this.labelEditorComponent.currentlyEditedSpanDataCheckResults.textCompression
          : this.labelEditorComponent.currentlyEditedSpanData.compression,
        this.labelEditorComponent.currentlyEditedSpanData.autoFormatByTextCheck,
        this.labelEditorComponent.currentlyEditedSpanDataCheckResults
          ? this.labelEditorComponent.currentlyEditedSpanDataCheckResults.containsErrors
          : this.labelEditorComponent.currentlyEditedSpanData.containsErrors,
        this.labelEditorComponent.currentlyEditedSpanDataCheckResults
          ? this.labelEditorComponent.currentlyEditedSpanDataCheckResults.containsStrictConformityErrors
          : this.labelEditorComponent.currentlyEditedSpanData.containsStrictConformityErrors,
        this.labelEditorComponent.currentlyEditedSpanDataCheckResults
          ? this.labelEditorComponent.currentlyEditedSpanDataCheckResults.errorTooltipText
          : this.labelEditorComponent.currentlyEditedSpanData.errorTooltipText,
        this.labelEditorComponent.currentlyEditedSpanData.font
      );
      return this.currentValue;
    };

    quillTableEditor.prototype.beginEditing = function (newInitialValue, event) {
      // need to enable full edit mode, otherwise handsontable does not correctly set (empty) content
      // when pressing char keys to begin editing of cells
      this.enableFullEditMode();
      Handsontable.editors.BaseEditor.prototype.beginEditing.call(this, newInitialValue, event);
    };

    quillTableEditor.prototype.setValue = function (value) {
      this.currentValue = value;
    };

    quillTableEditor.prototype.open = function () {
      if (this._opened) {
        return;
      } // because of incorrect calls by handsontable to open/close methods
      this._opened = true;
      const spanData: ISpanData = this.labelEditorComponent.tableProvider.getSpanData(this.row, this.col);
      let selectionTextIndex: number = NaN;
      if (this.currentValue === '') {
        // text content should be cleared, but we want to keep formatting
        this.select.querySelector('.ql-editor').innerHTML = spanData.html5data;
        this.editor.update('api');
        const format = this.editor.getFormat(0, 1);
        this.editor.deleteText(0, this.editor.getLength(), 'api');
        this.editor.insertText(0, '\uFEFF', 'api');
        this.editor.formatText(0, this.editor.getLength(), format, 'api');
        selectionTextIndex = 0;
        this.editor.update('api');
      } else {
        this.select.querySelector('.ql-editor').innerHTML = this.currentValue;
        this.editor.update('api');
        ConstraintCheckUtils.performValidationOnCurrentTextInEditor(
          this.labelEditorComponent.useStrictTextCheckConformity(),
          this.labelEditorComponent.localeResourceManager,
          this.editor,
          spanData
        );
        const textLength: number = this.editor.getLength() - 1; // -1 for excluding last newline charachter (\n) in text
        if (textLength > 1 && this.editor.getText(textLength - 1, 1) === '\uFEFF') {
          selectionTextIndex = textLength - 1;
        }
      }
      this.refreshDimensions();
      // this.select.classList.add('label-editor-custom-background'); IE 11 does not support custom variables
      if (spanData.labelFormat.backgroundColor) {
        // IE 11 does not support custom variables:
        // this.select.style.setProperty('--le-custom-background-color', spanData.labelFormat.backgroundColor);
        this.select.style.backgroundColor = spanData.labelFormat.backgroundColor;
      } else {
        // IE 11 does not support custom variables:
        // this.select.style.setProperty('--le-custom-background-color', '#FFFFFF');
        this.select.style.backgroundColor = '#FFFFFF';
      }
      this.select.style.display = '';
      // put cursor at the end of current text, also fixes one instance of problem in IE11 where resize rectangle appears:
      if (isNaN(selectionTextIndex)) {
        this.editor.setSelection(this.editor.getLength(), 0);
      } else {
        this.editor.setSelection(selectionTextIndex, 0);
      }
    };

    quillTableEditor.prototype.close = function (skipApplyChangedValue: boolean = false) {
      if (!this._opened) {
        return;
      } // because of incorrect calls to open/close methods, due to close when scrolling outside view
      this._opened = false;
      this.select.style.display = 'none';
      this.labelEditorComponent.isPasteInLabelEditorAllowed = false;
      ConstraintCheckUtils.clearErrorHighlightsInEditor(this.editor, true);
      if (!skipApplyChangedValue) {
        this.editor.update('api');
        this.currentValue = this.select.querySelector('.ql-editor').innerHTML;
      }
      // to capture format changes while editor is hidden (no specific part of text should be selected in such case):
      this.editor.setSelection(0, this.editor.getLength());
    };

    quillTableEditor.prototype.focus = function () {
      this.editor.focus();
    };

    quillTableEditor.prototype.refreshValue = function () {
      const sourceData = this.instance.getSourceDataAtCell(this.row, this.prop);
      this.originalValue = sourceData;

      this.setValue(sourceData);
      this.refreshDimensions();
    };

    quillTableEditor.prototype.refreshDimensions = function () {
      if (this.state !== 'STATE_EDITING') {
        return;
      }
      this.TD = this.getEditedCell();

      // TD is outside of the viewport.
      if (!this.TD) {
        // close must be kept, because otherwise editor is visible when cell is scrolled outside of view
        // this.close(false);
        // just calling close, as done by other editors, seems not to be the correct way,
        // as handsontable does not directly update value to spanData in such cases.
        // This would lead to showing incorrect value, which only gets corrected later on next user interaction.
        // Thus we destroy (close) editor this way:
        this.instance.destroyEditor();
        return;
      }
      const currentOffset: any = Handsontable.dom.offset(this.TD);
      const containerOffset: any = Handsontable.dom.offset(this.instance.rootElement);
      const scrollableContainer: HTMLElement = Handsontable.dom.getScrollableElement(this.TD);
      const editorSection = this.checkEditorSection();
      let width: number = Handsontable.dom.outerWidth(this.TD) + 1;
      let height: number = Handsontable.dom.outerHeight(this.TD) + 1;
      let editTop = currentOffset.top - containerOffset.top - 1 - (scrollableContainer.scrollTop || 0);
      let editLeft = currentOffset.left - containerOffset.left - 1 - (scrollableContainer.scrollLeft || 0);
      let cssTransformOffset;

      switch (editorSection) {
        case 'top':
          cssTransformOffset = Handsontable.dom.getCssTransform(this.instance.view.wt.wtOverlays.topOverlay.clone.wtTable.holder.parentNode);
          break;
        case 'left':
          cssTransformOffset = Handsontable.dom.getCssTransform(this.instance.view.wt.wtOverlays.leftOverlay.clone.wtTable.holder.parentNode);
          break;
        case 'top-left-corner':
          cssTransformOffset = Handsontable.dom.getCssTransform(
            this.instance.view.wt.wtOverlays.topLeftCornerOverlay.clone.wtTable.holder.parentNode
          );
          break;
        case 'bottom-left-corner':
          cssTransformOffset = Handsontable.dom.getCssTransform(
            this.instance.view.wt.wtOverlays.bottomLeftCornerOverlay.clone.wtTable.holder.parentNode
          );
          break;
        case 'bottom':
          cssTransformOffset = Handsontable.dom.getCssTransform(this.instance.view.wt.wtOverlays.bottomOverlay.clone.wtTable.holder.parentNode);
          break;
        default:
          break;
      }
      if (this.instance.getSelectedLast()[0] === 0) {
        editTop += 1;
      }

      if (this.instance.getSelectedLast()[1] === 0) {
        editLeft += 1;
      }

      const selectStyle = this.select.style;

      if (cssTransformOffset && cssTransformOffset !== -1) {
        selectStyle[cssTransformOffset[0]] = cssTransformOffset[1];
      } else {
        Handsontable.dom.resetCssTransform(this.select);
      }
      const cellComputedStyle = getComputedStyle(this.TD);

      if (parseInt(cellComputedStyle.borderTopWidth, 10) > 0) {
        height -= 1;
      }
      if (parseInt(cellComputedStyle.borderLeftWidth, 10) > 0) {
        width -= 1;
      }

      // apply corrections (different measurement/render techniques between editor and renderer)
      editTop += 1;
      editLeft += 1;
      width -= 2;
      height -= 2;

      const spanData: ISpanData = this.labelEditorComponent.tableProvider.getSpanData(this.row, this.col);
      const maxMultilineCount: number = labelEditorComponent.tableProvider.getMaxMultilineCount(this.row, this.col);
      const colWidth: number = this.instance.getColWidth(this.col);
      // see also: quill-table-renderer.ts
      let rowHeight: number = 0;
      for (let i: number = 0; i < spanData.rowSpan; i++) {
        // we need to reduce row height here by 1, because td elements will (potentially) use bottom border in contrast to row headers.
        // For row headers height was increased because of that by 1, this is why we need to reduce height here again for td-elements:
        rowHeight += this.instance.getRowHeight(this.row + i) - 1;
      }
      // for last spanned row, we dont cross (span) last border. Such border (height 1px) will be visible. Need to substract last border:
      rowHeight--;

      const cellInsets: any = LabelEditorUtils.calculateCellInsets(spanData, maxMultilineCount, colWidth, rowHeight);
      // apply insets:
      editTop += cellInsets.insetTop;
      editLeft += cellInsets.insetLeft;
      height -= cellInsets.insetTop;
      height -= cellInsets.insetBottom;
      width -= cellInsets.insetLeft;
      width -= cellInsets.insetRight;

      // consider custom border:
      const qlEditor = this.select.querySelector('.ql-editor');
      if (spanData && spanData.labelFormat.borderColor) {
        editTop += 2; // 2px for custom border
        editLeft += 2; // 2px for custom border
        height -= 4; // 2x 2px for top and bottom
        width -= 4; // 2x 2px for left and right
        if (qlEditor) {
          qlEditor.style.padding = '0px 3px 0px 2px';
        }
      } else {
        if (qlEditor) {
          qlEditor.style.padding = '2px 5px 2px 4px';
        }
      }

      selectStyle.height = `${height}px`;
      selectStyle.minWidth = `${width}px`;
      selectStyle.width = `${width}px`;
      selectStyle.top = `${editTop}px`;
      selectStyle.left = `${editLeft}px`;
      selectStyle.margin = '0px';
    };

    quillTableEditor.prototype.getEditedCell = function () {
      const editorSection = this.checkEditorSection();
      let editedCell;

      switch (editorSection) {
        case 'top':
          editedCell = this.instance.view.wt.wtOverlays.topOverlay.clone.wtTable.getCell({
            row: this.row,
            col: this.col,
          });
          this.select.style.zIndex = 101;
          break;
        case 'corner':
          editedCell = this.instance.view.wt.wtOverlays.topLeftCornerOverlay.clone.wtTable.getCell({
            row: this.row,
            col: this.col,
          });
          this.select.style.zIndex = 103;
          break;
        case 'left':
          editedCell = this.instance.view.wt.wtOverlays.leftOverlay.clone.wtTable.getCell({
            row: this.row,
            col: this.col,
          });
          this.select.style.zIndex = 102;
          break;
        default:
          editedCell = this.instance.getCell(this.row, this.col);
          this.select.style.zIndex = '';
          break;
      }
      return editedCell !== -1 && editedCell !== -2 ? editedCell : void 0;
    };

    return quillTableEditor;
  }
}
